/**
 * CommissionTableList - Lista de tabelas de comissão agrupadas
 * Usa collapsible simples ao invés de Accordion para evitar overflow-hidden
 * que corta o CommissionInlineEditor quando ele aparece dinamicamente
 */
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { DollarSign, Plus, FileText, Trash2, ChevronRight, ChevronDown } from "lucide-react";
import { CommissionInlineEditor } from "./CommissionInlineEditor";

export function CommissionTableList({
  tabelasFiltradas,
  tabelasAgrupadasPorTipo,
  bancos,
  loading,
  tabelaExpandida,
  comissoesEditando,
  onToggleTabelaExpandida,
  onSalvarComissoes,
  onExcluirTabela,
  onAbrirNovaTabela,
  onAbrirImportacao,
  onAdicionarLinhaInline,
  onAtualizarComissaoInline,
  onExcluirLinhaInline,
  onExcluirMultiplasLinhas,
  getNomeInstituicao
}) {
  const [openGroups, setOpenGroups] = useState([]);

  const toggleGroup = (tipo) => {
    setOpenGroups(prev => 
      prev.includes(tipo) ? prev.filter(t => t !== tipo) : [...prev, tipo]
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="text-center py-12 text-slate-500">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="bg-gradient-to-r from-emerald-100 to-teal-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-700" />
              Tabelas de Comissão
            </CardTitle>
            <CardDescription>
              {tabelasFiltradas.length} tabela(s) encontrada(s)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {tabelasFiltradas.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg">
            <DollarSign className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-medium mb-2">Nenhuma tabela encontrada</p>
            <p className="text-slate-400 text-sm mb-4">Crie uma nova tabela de comissão ou ajuste os filtros</p>
            <Button 
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              onClick={onAbrirNovaTabela}
              data-testid="btn-criar-primeira-tabela"
            >
              <Plus className="h-4 w-4" />
              Criar Primeira Tabela
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(tabelasAgrupadasPorTipo).map(([tipo, tabelas]) => {
              const isGroupOpen = openGroups.includes(tipo);
              return (
                <div key={tipo} className="border rounded-lg" data-testid={`grupo-tipo-${tipo}`}>
                  {/* Group Header - Trigger */}
                  <button
                    className="flex w-full items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    onClick={() => toggleGroup(tipo)}
                    data-testid={`btn-toggle-grupo-${tipo}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge className="bg-orange-100 text-orange-700">{tipo}</Badge>
                      <span className="text-sm text-slate-600">{tabelas.length} tabela(s)</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${isGroupOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Group Content - No overflow-hidden issues */}
                  {isGroupOpen && (
                    <div data-testid={`grupo-content-${tipo}`}>
                      {tabelas.map((tabela) => {
                        const banco = bancos.find(b => b.id === tabela.banco_id);
                        const isExpanded = tabelaExpandida === tabela.id;
                        
                        return (
                          <div key={tabela.id} className="border-t" data-testid={`tabela-row-${tabela.id}`}>
                            {/* Header da Tabela - Clicável */}
                            <div 
                              className={`flex items-center justify-between p-3 cursor-pointer hover:bg-emerald-50 ${isExpanded ? 'bg-emerald-100' : 'bg-white'}`}
                              onClick={() => onToggleTabelaExpandida(tabela)}
                              data-testid={`tabela-header-${tabela.id}`}
                            >
                              <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-emerald-600" />
                                  <span className="font-medium">{tabela.nome || "Sem nome"}</span>
                                </div>
                                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
                                  {banco?.codigo ? `${banco.codigo} - ${banco.nome}` : banco?.nome || tabela.banco_id}
                                </Badge>
                                {tabela.instituicao && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                    {tabela.instituicao}
                                  </Badge>
                                )}
                                {tabela.correspondente && (
                                  <Badge className="bg-purple-100 text-purple-700 border border-purple-200">
                                    {tabela.correspondente}
                                  </Badge>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  {(tabela.prazos || []).slice(0, 3).map((prazo, idx) => (
                                    <Badge key={`prazo-${tabela.id}-${idx}`} variant="secondary" className="text-xs">{prazo}</Badge>
                                  ))}
                                </div>
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  {(isExpanded ? comissoesEditando : tabela.comissoes)?.length || 0} regras
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:bg-red-50"
                                  onClick={(e) => { e.stopPropagation(); onExcluirTabela(tabela.id); }}
                                  title="Excluir"
                                  data-testid={`btn-excluir-tabela-${tabela.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Editor Inline - Aparece ao clicar */}
                            {isExpanded && (
                              <CommissionInlineEditor
                                tabela={tabela}
                                comissoesEditando={comissoesEditando}
                                onAbrirImportacao={onAbrirImportacao}
                                onAdicionarLinha={onAdicionarLinhaInline}
                                onSalvar={onSalvarComissoes}
                                onAtualizarComissao={onAtualizarComissaoInline}
                                onExcluirLinha={onExcluirLinhaInline}
                                onExcluirMultiplas={onExcluirMultiplasLinhas}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
